































































































































import { ClientAccountsApi } from '@/api/client-accounts';
import { Forbidden } from '@/api/responses/forbidden';
import bookAppointmentDialog from '@/components/appointments/book-appointment-dialog.vue';
import addClientDialog from '@/components/client/add-client/add-client-dialog.vue';
import clientsTable from '@/components/client/list-clients/clients-table.vue';
import addPartnerDialog from '@/components/client/view-client/add-partner-dialog.vue';
import logInteractionDialog from '@/components/client/view-client/log-interaction-dialog.vue';
import loading from '@/components/loading.vue';
import layout from '@/layouts/app.vue';
import { ClientAccount } from '@/shared/clients/client-account';
import { Interaction } from '@/shared/interaction';
import { PartnerPractice } from '@/shared/partner-practice';
import { ClientStatusType } from '@/shared/types/client-status-type';
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const partnerPracticesModule = namespace('partnerPractices');
const typesModule = namespace('types');

const clientApi = new ClientAccountsApi();

@Component({
    components: {
        addClientDialog,
        addPartnerDialog,
        bookAppointmentDialog,
        clientsTable,
        layout,
        loading,
        logInteractionDialog,
    },
})
export default class ListClients extends Vue {
    @partnerPracticesModule.Getter('partnerPracticesForCurrentUser') private partnerPractices!: Array<PartnerPractice>;

    @typesModule.Getter('activeClientStatusTypes') public statusTypes!: Array<ClientStatusType>;

    private currentPage: number = 1;
    private pageSize: number = 10;
    private total: number = 0;
    private clients: Array<ClientAccount> = new Array<ClientAccount>();
    private selectedClient: ClientAccount | null = null;
    private isLoading: boolean = false;
    private partnerPracticeId: string | null = null;
    private accountNumber: number | null = null;
    private lastName: string | null = null;
    private firstName: string | null = null;
    private dateOfBirth: string | null = null;
    private postcode: string | null = null;
    private accountStatusId: string | null = null;
    private originalSourceId: string | null = null;
    private telephone: string | null = null;
    private rlgReference: string | null = null;
    private partnerPracticeReference: string | null = null;
    private addClientDialogVisible: boolean = false;
    private bookAppointmentDialogVisible: boolean = false;
    private addPartnerDialogVisible: boolean = false;
    private logInteractionDialogVisible: boolean = false;
    private emptyText: string | null = 'Please enter some search filters and click Filter to show clients';

    private async onPageChanged(page: number): Promise<void> {
        if (page === this.currentPage) {
            return;
        }

        this.currentPage = page;
        this.saveQueryString();
        await this.fetchClientsAsync();
    }

    private async onSearch(): Promise<void> {
        if (!await this.$validator.validateAll()) { return; }

        this.currentPage = 1;
        this.saveQueryString();
        await this.fetchClientsAsync();
    }

    private async fetchClientsAsync(): Promise<void> {
        if (!await this.$validator.validateAll()) { return; }

        try {
            this.isLoading = true;

            const result = await clientApi.getClientAccountsAsync({
                page: this.currentPage,
                pageSize: this.pageSize,
                accountNo: this.accountNumber,
                firstName: this.firstName,
                lastName: this.lastName,
                dateOfBirth: this.dateOfBirth,
                postcode: this.postcode != null ? this.postcode.replace(/ /g, '') : null,
                telephone: this.telephone,
                rlgReference: this.rlgReference,
                partnerPracticeReference: this.partnerPracticeReference,
                accountStatusId: this.accountStatusId,
                originalSourceId: this.originalSourceId,
                partnerPracticeId: this.partnerPractices.length === 1 ? this.partnerPractices[0].id! : this.partnerPracticeId,
            });

            this.emptyText = 'No clients found matching the search filters';

            this.clients = result.clients;
            this.total = result.total;
        } catch (e) {
            if (e instanceof Forbidden) {
                this.$router.push({ name: 'forbidden' });
            } else {
                this.$snackbar.open({ message: `Could not load clients.`, type: 'is-danger' });
            }
        }

        this.isLoading = false;
    }

    private onClientCreated(result: { id: string, isNew: boolean }): void {
        if (result.isNew) {
            this.$snackbar.open({ message: 'Client account successfully created.', type: 'is-success' });
        } else {
            this.$snackbar.open({ message: 'Client account successfully merged and updated.', type: 'is-success' });
        }

        this.$router.push({ name: 'view-client', params: { id: result.id } });
    }

    private async onAppointmentBooked(): Promise<void> {
        this.selectedClient = null;
        this.$snackbar.open({ message: `Appointment successfully booked.`, type: 'is-success' });
        this.onCancel();
    }

    private async onInteractionLogged(interaction: Interaction): Promise<void> {
        this.selectedClient = null;
        this.$snackbar.open({ message: `Interaction successfully logged.`, type: 'is-success' });

        this.onCancel();
        await this.fetchClientsAsync();
    }

    private async onPartnerAdded(partner: ClientAccount): Promise<void> {
        try {
            await clientApi.assignClientPartnerAsync(this.selectedClient!, partner.id!);

            this.$snackbar.open({ message: `Partner successfully added.`, type: 'is-success' });
        } catch (e) {
            this.$snackbar.open({ message: 'Could not assign partner, please try again.', type: 'is-danger' });
        }

        this.onCancel();
        await this.fetchClientsAsync();
    }

    private showAddClientDialog(): void {
        this.addClientDialogVisible = true;
    }

    private showBookAppointmentDialog(client: ClientAccount | null): void {
        this.selectedClient = client;
        this.bookAppointmentDialogVisible = true;
    }

    private showAddPartnerDialog(client: ClientAccount | null): void {
        this.selectedClient = client;
        this.addPartnerDialogVisible = true;
    }

    private showLogInteractionDialog(client: ClientAccount | null): void {
        this.selectedClient = client;
        this.logInteractionDialogVisible = true;
    }

    private onCancel(): void {
        this.addClientDialogVisible = false;
        this.bookAppointmentDialogVisible = false;
        this.addPartnerDialogVisible = false;
        this.logInteractionDialogVisible = false;
    }

    private canBookAppointment(client: ClientAccount): boolean {
        return !client.doNotContact
            && !client.isDeceased
            && !client.isAccountArchived;
    }

    private canAddPartner(client: ClientAccount): boolean {
        return client.partner == null;
    }

    private setFilters(queryString: any): void {
        this.partnerPracticeId = queryString.partnerPracticeId;
        this.accountNumber = queryString.acountNumber;
        this.lastName = queryString.lastName;
        this.firstName = queryString.firstName;
        this.dateOfBirth = queryString.dateOfBirthMonth != null && queryString.dateOfBirthDay != null && queryString.dateOfBirthYear != null ?
            `${+queryString.dateOfBirthMonth + 1}/${queryString.dateOfBirthDay}/${queryString.dateOfBirthYear}` :
            null;
        this.postcode = queryString.postcode;
        this.accountStatusId = queryString.accountStatusId;
        this.originalSourceId = queryString.originalSourceId;
        this.telephone = queryString.telephone;
        this.rlgReference = queryString.rlgReference;
        this.partnerPracticeReference = queryString.partnerPracticeReference;
        this.currentPage = +queryString.currentPage;
    }

    private clearFilters(): void {

        this.partnerPracticeId = null;
        this.accountNumber = null;
        this.lastName = null;
        this.firstName = null;
        this.dateOfBirth = null;
        this.postcode = null;
        this.accountStatusId = null;
        this.originalSourceId = null;
        this.telephone = null;
        this.rlgReference = null;
        this.partnerPracticeReference = null;
    }

    private async saveQueryString(): Promise<void> {
        const queryString = {
            name: 'clients',
            query: {
                currentPage: this.currentPage.toString(),
                firstName: this.firstName,
                lastName: this.lastName,
                dateOfBirthDay: this.dateOfBirth != null ? new Date(this.dateOfBirth!).getDate().toString() : null,
                dateOfBirthMonth: this.dateOfBirth != null ? new Date(this.dateOfBirth!).getMonth().toString() : null,
                dateOfBirthYear: this.dateOfBirth != null ? new Date(this.dateOfBirth!).getFullYear().toString() : null,
                postcode: this.postcode != null ? this.postcode.replace(/ /g, '') : null,
                telephone: this.telephone,
                rlgReference: this.rlgReference,
                partnerPracticeReference: this.partnerPracticeReference,
                accountStatusId: this.accountStatusId,
                originalSourceId: this.originalSourceId,
                partnerPracticeId: this.partnerPracticeId,
            },
        };

        if (JSON.stringify(this.$route.query) === JSON.stringify(queryString.query)) {
            return;
        }

        this.$router.push(queryString);
    }

    private async mounted(): Promise<void> {
        this.isLoading = true;

        await Promise.all([
            this.$store.dispatch(`partnerPractices/loadPartnerPracticesAsync`),
            this.$store.dispatch(`types/loadTypesAsync`),
        ]);

        const queryString = this.$route.query;

        if (Object.keys(queryString).length > 0) {
            this.setFilters(queryString);
            await this.fetchClientsAsync();
        }

        this.isLoading = false;
    }
}
